import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { AlertCircle, ArrowUpRight, Check, Github, Linkedin, Mail, Send } from "lucide-react";
import Card, { Kicker, Rows, Row } from "../brand/Surface";
import Action from "../brand/Action";
import Reveal from "../brand/Reveal";
import Mark from "../brand/Mark";
import portrait from "../../images/me/casper.webp";
import { ME } from "../../data/cv";
import { cn } from "../../lib/utils";

const EMAILJS = {
  service: "service_r1qmz1g",
  template: "template_3nrdgfj",
  key: "BGkOpbq_V624LS8c9",
};

const SUBJECTS = [
  { id: "werk", label: "Een opdracht" },
  { id: "vraag", label: "Een vraag" },
  { id: "hallo", label: "Gewoon hallo" },
];

export default function Contact() {
  const form = useRef(null);
  const [subject, setSubject] = useState("werk");
  const [agreed, setAgreed] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Contact · Casper De Bock";
  }, []);

  const send = (e) => {
    e.preventDefault();
    if (!agreed) return;
    setSending(true);
    setStatus(null);

    emailjs.sendForm(EMAILJS.service, EMAILJS.template, form.current, EMAILJS.key).then(
      () => {
        setStatus({ ok: true, message: "Verstuurd. Ik laat binnen een dag iets weten." });
        form.current.reset();
        setSubject("werk");
        /* reset() clears the box in the DOM; the state has to follow or the
           button stays enabled against an unticked checkbox. */
        setAgreed(false);
        setSending(false);
      },
      () => {
        setStatus({
          ok: false,
          message: `Dat ging niet. Mail me rechtstreeks op ${ME.email}.`,
        });
        setSending(false);
      },
    );
  };

  return (
    <div className="grid gap-4 desk:grid-cols-12">
      {/* ------------------------------------------------------- the ask */}
      <Reveal className="desk:col-span-5">
        <Card tone="solid" pad="lg" lit className="relative h-full overflow-hidden">
          <span
            aria-hidden
            className="pointer-events-none absolute -top-20 -right-16 size-[260px] rounded-full bg-accent/20 blur-[80px]"
          />
          <div className="relative z-10 flex h-full flex-col">
            {/* The one place a face earns its space: you are about to write to
                a person, and it helps to know which one. Framed in the same
                skewed slab as the mark and the buttons, so a photograph does
                not arrive as the only unstyled rectangle on the site. */}
            <div className="flex items-center gap-4">
              <span className="slab block size-[78px] shrink-0 overflow-hidden border border-edge">
                {/* Counter-skewed and scaled up: at 1:1 the frame is mostly
                    café, and the point of a portrait this small is the face.
                    The object-position centres on it rather than on the
                    middle of the photograph. */}
                <img
                  src={portrait}
                  alt="Casper De Bock"
                  width="900"
                  height="863"
                  className="size-full object-cover object-[54%_40%] [transform:skewX(6deg)_scale(1.75)]"
                />
              </span>
              <Mark size={30} decorative />
            </div>

            <Kicker className="mt-7 text-muted">Contact</Kicker>
            <h1 className="max-w-[12ch] text-[clamp(30px,6vw,52px)] text-fg">
              Wat loopt er mis?
            </h1>
            <p className="mt-4 max-w-[38ch] text-[15px] text-body">
              Vertel het in een paar zinnen. Als ik het kan oplossen zeg ik hoe en wat het ongeveer
              kost — en als ik het niet kan, zeg ik dat ook.
            </p>

            <div className="mt-auto pt-10">
              <Rows>
                <Row
                  className="border-hair"
                  meta={<span className="text-muted">binnen een dag</span>}
                >
                  <span className="text-fg">Antwoord</span>
                </Row>
                <Row className="border-hair" meta={<span className="text-muted">gratis</span>}>
                  <span className="text-fg">Eerste gesprek</span>
                </Row>
              </Rows>

              <div className="mt-6 flex flex-wrap gap-1">
                {[
                  { href: `mailto:${ME.email}`, icon: Mail, label: "Mail" },
                  { href: ME.github, icon: Github, label: "GitHub" },
                  { href: ME.linkedin, icon: Linkedin, label: "LinkedIn" },
                ].map(({ href, icon: Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer noopener"
                    aria-label={label}
                    className="tap grid size-11 place-items-center rounded-full text-muted transition-colors hover:text-fg"
                  >
                    <Icon aria-hidden className="size-[18px]" strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </Reveal>

      {/* ---------------------------------------------------------- form */}
      <Reveal className="desk:col-span-7" delay={0.06}>
        <Card pad="lg">
          <h2 className="font-display text-[22px] leading-tight font-medium tracking-[-0.05em] text-fg">
            Stuur een bericht
          </h2>

          <form ref={form} onSubmit={send} noValidate className="mt-7">
            {/* Goes along in the mail so I know what it is about before I open
                it. Hidden input rather than a select: three buttons are one tap
                and a select is three. */}
            <input type="hidden" name="subject" value={subject} />

            <span className="kicker mb-2 block">Waarover gaat het</span>
            <div className="rail gap-1.5 pb-1">
              {SUBJECTS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSubject(s.id)}
                  aria-pressed={subject === s.id}
                  className={cn(
                    "tap shrink-0 rounded-full border px-4 py-2.5 text-[13px] font-semibold whitespace-nowrap transition-colors",
                    subject === s.id
                      ? "border-transparent bg-fg text-bg"
                      : "border-line bg-surface-solid text-muted hover:text-fg",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <Field id="c-name" name="name" label="Je naam" placeholder="Jan Peeters" required />
              <Field
                id="c-email"
                name="email"
                type="email"
                label="Je e-mail"
                placeholder="jan@bedrijf.be"
                required
              />
            </div>

            <label htmlFor="c-message" className="kicker mt-5 mb-2 block">
              Je bericht
            </label>
            <textarea
              id="c-message"
              name="message"
              rows={6}
              required
              placeholder="Wat loopt er mis, en voor wie?"
              className="w-full resize-y rounded-brand-sm border border-line bg-surface-solid p-4 outline-none placeholder:text-muted focus-visible:border-accent"
            />

            {/* Real consent: a box you tick yourself, that actually holds the
                button shut until you do. A line of small print under the
                button ("door te versturen gaat u akkoord") is not a choice. */}
            <label
              htmlFor="c-consent"
              className="tap mt-6 flex cursor-pointer items-start gap-3 text-[13px]"
            >
              <input
                id="c-consent"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 size-[18px] shrink-0 cursor-pointer accent-[#CCFF00]"
              />
              <span>
                Ik ga ermee akkoord dat mijn naam, e-mailadres en bericht verwerkt worden om mijn
                vraag te beantwoorden.{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-semibold text-accent-text underline underline-offset-2"
                >
                  Privacyverklaring
                </Link>
                .
              </span>
            </label>

            <Action
              type="submit"
              variant="accent"
              size="lg"
              disabled={sending || !agreed}
              className="mt-5 w-full sm:w-auto"
            >
              <Send aria-hidden className="size-4" strokeWidth={2} />
              {sending ? "Bezig…" : "Versturen"}
            </Action>

            {status && (
              <p
                role={status.ok ? "status" : "alert"}
                aria-live="polite"
                className={cn(
                  "mt-4 flex items-start gap-2 text-[13.5px] font-semibold",
                  status.ok ? "text-accent-text" : "text-[#C6361A]",
                )}
              >
                {status.ok ? (
                  <Check aria-hidden className="mt-0.5 size-4 shrink-0" strokeWidth={2.5} />
                ) : (
                  <AlertCircle aria-hidden className="mt-0.5 size-4 shrink-0" strokeWidth={2} />
                )}
                {status.message}
              </p>
            )}

            <p className="mt-5 text-[12.5px]">
              Je bericht komt rechtstreeks in mijn mailbox terecht en gaat nergens anders heen.
              Liever zelf mailen?{" "}
              <a
                href={`mailto:${ME.email}`}
                className="font-semibold text-accent-text underline underline-offset-2"
              >
                {ME.email}
                <ArrowUpRight aria-hidden className="inline size-3.5" strokeWidth={2} />
              </a>
            </p>
          </form>
        </Card>
      </Reveal>
    </div>
  );
}

function Field({ id, name, label, type = "text", placeholder, required }) {
  return (
    <div>
      <label htmlFor={id} className="kicker mb-2 block">
        {label}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="h-12 w-full rounded-brand-sm border border-line bg-surface-solid px-4 outline-none placeholder:text-muted focus-visible:border-accent"
      />
    </div>
  );
}
